import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { faCheckCircle, faExclamationTriangle, faBriefcase, faMapMarkerAlt, faUserTie } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import api from '../../../../../services/api';
import { formatNumber, parseLocaleNumber } from '../../../../../utils/numberFormatter';
import Swal from 'sweetalert2';
import { useSuggestedData } from '../../../../../context/SuggestedDataContext';
import { Modal } from 'react-bootstrap';
import { DataSourceBadge, EditButton } from '../../../../Common';

// Configuración para selección automática de datos de bureaus
const PRIORITIZED_DATA_SOURCES = ['siisa']; // Lista de sources priorizados, ordenados por prioridad
const RENEWER_CLIENT_TYPE = 'RENEWER';

const provinces = [{"id":2,"name":"Buenos Aires"},{"id":3,"name":"Capital Federal"},{"id":10,"name":"Catamarca"},{"id":8,"name":"Chaco"},{"id":19,"name":"Chubut"},{"id":22,"name":"Cordoba"},{"id":21,"name":"Corrientes"},{"id":5,"name":"Entre Rios"},{"id":14,"name":"Formosa"},{"id":23,"name":"Jujuy"},{"id":11,"name":"La Pampa"},{"id":6,"name":"La Rioja"},{"id":12,"name":"Mendoza"},{"id":13,"name":"Misiones"},{"id":15,"name":"Neuquen"},{"id":16,"name":"Rio Negro"},{"id":1,"name":"Salta"},{"id":9,"name":"San Juan"},{"id":4,"name":"San Luis"},{"id":24,"name":"Santa Cruz"},{"id":17,"name":"Santa Fe"},{"id":7,"name":"Santiago del Estero"},{"id":20,"name":"Tierra del Fuego"},{"id":18,"name":"Tucuman"}];

const EmploymentInfo = forwardRef((props, ref) => {
  const {
    formData,
    handleChange,
    activeSection,
    sectionName,
    setSectionValidity,
    sectionValidity,
    handleSectionClick,
    isLoadingSuggestions,
    displayMode = 'accordion',
    clientData
  } = props;

  const isContinuousMode = displayMode === 'continuous';

  const { suggestedData, selectedData, setSelectedData, setSuggestedData } = useSuggestedData();
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [workLocalities, setWorkLocalities] = useState([]);
  const [isLoadingLocalities, setIsLoadingLocalities] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [showNewEmploymentModal, setShowNewEmploymentModal] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isEditingEmployment, setIsEditingEmployment] = useState(false);
  const [editingEmploymentData, setEditingEmploymentData] = useState(null);

  const suggestions = suggestedData.employment || [];

  // Función para auto-seleccionar datos del bureau priorizado para clientes renovadores
  const autoSelectPrioritizedData = () => {
    // Solo aplicar para clientes renovadores
    if (clientData?.client_type?.code !== RENEWER_CLIENT_TYPE) {
      return;
    }

    // Solo aplicar si no hay nada seleccionado aún
    if (selectedData.employment) {
      return;
    }

    // Buscar el dato más reciente entre todos los sources priorizados
    const prioritizedSuggestions = suggestions
      .filter(suggestion => {
        // Verificar si el dato viene de algún source priorizado
        return PRIORITIZED_DATA_SOURCES.some(source => 
          suggestion.source?.toLowerCase() === source.toLowerCase()
        );
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const mostRecentPrioritized = prioritizedSuggestions[0];
    
    if (mostRecentPrioritized) {
      handleUseExisting(mostRecentPrioritized);
    }
  };

  // Filtrar duplicados, sugerencias reemplazadas y ordenar por fecha (más reciente primero)
  const uniqueSuggestions = suggestions
    .filter((sugg, idx, arr) => arr.findIndex(s => s.id === sugg.id) === idx)
    .filter(sugg => !sugg.replacedBy) // Ocultar sugerencias que han sido reemplazadas
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Obtener las sugerencias a mostrar
  const displayedSuggestions = showAllSuggestions 
    ? uniqueSuggestions 
    : uniqueSuggestions.filter((suggestion, index) => 
        index === 0 || // Siempre mostrar la más reciente
        suggestion.id.toString().startsWith('new_') || // Mostrar las nuevas si hay ocultas
        suggestion.id.toString().startsWith('edited_') // Mostrar las editadas
      );

  const requiredFields = [
    'companyName',
    'entryDate',
    'workAddress',
    'workProvince',
    'workLocality',
    'workPostalCode'
  ];

  useEffect(() => {
    if (activeSection === sectionName && (selectedData.employment || touchedFields.companyName)) {
      validateSection();
    }
  }, [activeSection, sectionName, selectedData.employment]);

  useEffect(() => {
    if (activeSection === sectionName && suggestions !== undefined && suggestions !== null) {
      setShowNewForm(suggestions.length === 0);
    }
  }, [activeSection, sectionName, suggestions]);

  // UseEffect para auto-seleccionar datos del bureau priorizado
  useEffect(() => {
    if (suggestions.length > 0 && clientData) {
      autoSelectPrioritizedData();
    }
  }, [suggestions, clientData, selectedData.employment]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    handleChange(e);
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'companyName':
        if (!value) {
          error = 'El nombre de la empresa es requerido';
        }
        break;
      case 'entryDate':
        if (!value) {
          error = 'La fecha de ingreso es requerida';
        } else if (new Date(value) > new Date()) {
          error = 'Ingrese una fecha de ingreso válida';
        }
        break;
      case 'workAddress':
        if (!value) {
          error = 'El domicilio laboral es requerido';
        }
        break;
      case 'workProvince':
        if (!value) {
          error = 'La provincia es requerida';
        }
        break;
      case 'workLocality':
        if (!value) {
          error = 'La localidad es requerida';
        }
        break;        
      case 'workPostalCode':
        if (!value) {
          error = 'El código postal laboral es requerido';
        }
        break;
      default:
        break;
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
    return error;
  };

  const handleUseExisting = (suggestion) => {
    setSelectedData(prev => ({
      ...prev,
      employment: suggestion.id
    }));
    
    // Update form data with the selected employment details
    handleChange({ target: { name: 'companyName', value: suggestion.employer.name } });
    handleChange({ target: { name: 'position', value: suggestion.job.title || '' } });
    handleChange({ target: { name: 'entryDate', value: suggestion.job.entry_date || suggestion.job.join_date } });
    
    // Handle address data based on whether it's a new or existing employment
    if (suggestion.id.toString().startsWith('new_')) {
      // For new employments
      handleChange({ target: { name: 'workAddress', value: suggestion.address.street } });
      handleChange({ target: { name: 'workProvince', value: suggestion.address.province } });
      handleChange({ target: { name: 'workLocality', value: suggestion.address.locality } });
      handleChange({ target: { name: 'workPostalCode', value: suggestion.address.postal_code } });
    } else {
      // For existing employments
      handleChange({ target: { name: 'workAddress', value: suggestion.employer.address } });
      handleChange({ target: { name: 'workProvince', value: suggestion.employer.province } });
      handleChange({ target: { name: 'workLocality', value: suggestion.employer.locality } });
      handleChange({ target: { name: 'workPostalCode', value: suggestion.employer.postal_code } });
    }
    
    setSectionValidity(prev => ({ ...prev, [sectionName]: true }));
    setShowNewForm(false);
  };

  const handleUseNewForm = () => {
    setFormSubmitted(false);
    setIsEditingEmployment(false);
    setEditingEmploymentData(null);
    
    // Limpiamos la selección
    setSelectedData(prev => ({
      ...prev,
      employment: null
    }));
    
    // Limpiamos el formulario
    handleChange({ target: { name: 'companyName', value: '' } });
    handleChange({ target: { name: 'position', value: '' } });
    handleChange({ target: { name: 'entryDate', value: '' } });
    handleChange({ target: { name: 'workAddress', value: '' } });
    handleChange({ target: { name: 'workProvince', value: '' } });
    handleChange({ target: { name: 'workLocality', value: '' } });
    handleChange({ target: { name: 'workPostalCode', value: '' } });
    
    // Limpiamos los campos tocados
    setTouchedFields({});
    
    // Limpiamos los errores
    setErrors({});
    
    // Limpiamos las localidades
    setWorkLocalities([]);
    
    setShowNewEmploymentModal(true);
  };

  const handleEditEmployment = async (suggestion) => {
    setIsEditingEmployment(true);
    setEditingEmploymentData(suggestion);
    setShowNewEmploymentModal(true);
    setFormSubmitted(false);
    
    // Precargar los datos del empleo
    const companyName = suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_')
      ? suggestion.employer.name
      : suggestion.employer.name;
    const position = suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_')
      ? suggestion.job.title
      : suggestion.job.title;
    const entryDate = suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_')
      ? suggestion.job.entry_date
      : suggestion.job.entry_date || suggestion.job.join_date;
    
    // Determinar la estructura de la dirección
    let workAddress, workProvince, workLocality, workPostalCode;
    if (suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_')) {
      // Para empleos nuevos/editados
      workAddress = suggestion.address.street;
      workProvince = suggestion.address.province;
      workLocality = suggestion.address.locality;
      workPostalCode = suggestion.address.postal_code;
    } else {
      // Para empleos existentes
      workAddress = suggestion.employer.address;
      workProvince = suggestion.employer.province;
      workLocality = suggestion.employer.locality;
      workPostalCode = suggestion.employer.postal_code;
    }
    
    // Precargar los datos básicos del formulario
    handleChange({ target: { name: 'companyName', value: companyName || '' } });
    handleChange({ target: { name: 'position', value: position || '' } });
    handleChange({ target: { name: 'entryDate', value: entryDate || '' } });
    handleChange({ target: { name: 'workAddress', value: workAddress || '' } });
    handleChange({ target: { name: 'workPostalCode', value: workPostalCode || '' } });
    
    // Encontrar el ID de la provincia
    let provinceId = workProvince;
    if (typeof workProvince === 'string' && isNaN(workProvince)) {
      const foundProvince = provinces.find(p => p.name === workProvince);
      provinceId = foundProvince ? foundProvince.id : workProvince;
    }
    
    handleChange({ target: { name: 'workProvince', value: provinceId || '' } });
    
    // Cargar las localidades de la provincia si es necesario
    if (provinceId) {
      const cachedLocalities = getLocalitiesFromCache(provinceId);
      if (cachedLocalities && cachedLocalities.length > 0) {
        setWorkLocalities(cachedLocalities);
      } else {
        setIsLoadingLocalities(true);
        try {
          const response = await api.get(`/provinces/${provinceId}/localities`);
          const loadedLocalities = response.data;
          setWorkLocalities(loadedLocalities);
          saveLocalitiesToCache(provinceId, loadedLocalities);
        } catch (error) {
          console.error('Error fetching localities:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudieron cargar las localidades. Por favor, intente nuevamente.',
          });
        } finally {
          setIsLoadingLocalities(false);
        }
      }
    }
    
    setErrors({});
    setTouchedFields({});
  };

  const handleCloseModal = () => {
    setFormSubmitted(false);
    setShowNewEmploymentModal(false);
    setIsEditingEmployment(false);
    setEditingEmploymentData(null);
    
    // Resetear todos los campos
    handleChange({ target: { name: 'companyName', value: '' } });
    handleChange({ target: { name: 'position', value: '' } });
    handleChange({ target: { name: 'entryDate', value: '' } });
    handleChange({ target: { name: 'workAddress', value: '' } });
    handleChange({ target: { name: 'workProvince', value: '' } });
    handleChange({ target: { name: 'workLocality', value: '' } });
    handleChange({ target: { name: 'workPostalCode', value: '' } });
    handleChange({ target: { name: 'workPhone', value: '' } });
    setErrors({});
    setTouchedFields({});
    setWorkLocalities([]);
  };

  const checkForDuplicates = () => {
    const normalizedCompanyName = String(formData.companyName).trim().toLowerCase();
    const normalizedPosition = String(formData.position || '').trim().toLowerCase();
    const normalizedWorkAddress = String(formData.workAddress).trim().toLowerCase();
    const normalizedProvince = provinces.find(p => p.id === Number(formData.workProvince))?.name.trim().toLowerCase();
    const normalizedLocality = workLocalities.find(l => l.id === Number(formData.workLocality))?.name.trim().toLowerCase();
    const normalizedPostalCode = String(formData.workPostalCode).trim().toLowerCase();

    return suggestions.some(suggestion => {
      const existingCompanyName = String(suggestion.employer?.name || '').trim().toLowerCase();
      const existingPosition = String(suggestion.job?.title || '').trim().toLowerCase();
      const existingWorkAddress = String(suggestion.address?.street || '').trim().toLowerCase();
      const existingProvince = String(suggestion.address?.province || '').trim().toLowerCase();
      const existingLocality = String(suggestion.address?.locality || '').trim().toLowerCase();
      const existingPostalCode = String(suggestion.address?.postal_code || '').trim().toLowerCase();

      const isMatch = existingCompanyName === normalizedCompanyName &&
                     existingPosition === normalizedPosition &&
                     existingWorkAddress === normalizedWorkAddress &&
                     existingProvince === normalizedProvince &&
                     existingLocality === normalizedLocality &&
                     existingPostalCode === normalizedPostalCode;

      return isMatch;
    });
  };

  const handleSaveNewEmployment = () => {
    setFormSubmitted(true);
    // Validar el formulario antes de guardar
    const isValid = validateSection();
    if (!isValid) {
      return;
    }

    // Check for duplicates solo si no estamos editando
    if (!isEditingEmployment) {
      const isDuplicate = checkForDuplicates();
      if (isDuplicate) {
        Swal.fire({
          icon: 'error',
          title: 'Empleo duplicado',
          text: 'Este empleo ya existe en el sistema.',
          confirmButtonText: 'Entendido'
        });
        return;
      }
    }

    // Obtener los nombres correctos de provincia y localidad
    const selectedProvince = provinces.find(p => p.id === Number(formData.workProvince));
    const selectedLocality = workLocalities.find(l => l.id === Number(formData.workLocality));

    // Crear el objeto del empleo (nuevo o editado)
    const employmentData = {
      id: isEditingEmployment ? `edited_${editingEmploymentData.id}_${Date.now()}` : `new_${Date.now()}`,
      employer: {
        name: formData.companyName
      },
      job: {
        title: formData.position || '',
        entry_date: formData.entryDate
      },
      address: {
        street: formData.workAddress,
        province: selectedProvince?.id || '',
        province_label: selectedProvince?.name || '',
        locality: selectedLocality?.id || '',
        locality_label: selectedLocality?.name || '',
        postal_code: formData.workPostalCode
      },
      created_at: new Date().toISOString(),
      // Marcar si es una edición para el badge
      isEdited: isEditingEmployment,
      originalId: isEditingEmployment ? editingEmploymentData.id : null
    };

    // Si estamos editando, marcar el empleo original como reemplazado
    let updatedSuggestions;
    if (isEditingEmployment) {
      updatedSuggestions = suggestions.map(suggestion => 
        suggestion.id === editingEmploymentData.id 
          ? { ...suggestion, replacedBy: employmentData.id }
          : suggestion
      );
      // Agregar el nuevo empleo editado
      updatedSuggestions = [employmentData, ...updatedSuggestions];
    } else {
      // Para nuevos empleos, simplemente agregar al inicio
      updatedSuggestions = [employmentData, ...suggestions];
    }
    
    // Seleccionar el empleo por defecto
    setSelectedData(prev => ({
      ...prev,
      employment: employmentData.id
    }));

    // Actualizar el contexto con las nuevas sugerencias
    setSuggestedData(prev => ({
      ...prev,
      employment: updatedSuggestions
    }));

    // IMPORTANTE: Actualizar el formData con los valores seleccionados
    handleChange({ target: { name: 'companyName', value: formData.companyName } });
    handleChange({ target: { name: 'position', value: formData.position || '' } });
    handleChange({ target: { name: 'entryDate', value: formData.entryDate } });
    handleChange({ target: { name: 'workAddress', value: formData.workAddress } });
    handleChange({ target: { name: 'workProvince', value: selectedProvince?.id || '' } });
    handleChange({ target: { name: 'workLocality', value: selectedLocality?.id || '' } });
    handleChange({ target: { name: 'workPostalCode', value: formData.workPostalCode } });

    // Cerrar el modal
    setShowNewEmploymentModal(false);
    setIsEditingEmployment(false);
    setEditingEmploymentData(null);

    // Limpiar el formulario
    setErrors({});
    setTouchedFields({});
    setWorkLocalities([]);

    // Mostrar mensaje de éxito
    Swal.fire({
      icon: 'success',
      title: isEditingEmployment ? 'Empleo editado' : 'Empleo agregado',
      text: isEditingEmployment ? 'La información laboral ha sido editada correctamente.' : 'La nueva información laboral ha sido agregada correctamente.',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const validateSection = () => {
    // Si hay un empleo seleccionado Y no estamos en modo de edición, validar como correcto
    if (selectedData.employment && !isEditingEmployment && !showNewEmploymentModal) {
      setSectionValidity((prev) => ({ ...prev, [sectionName]: true }));
      return true;
    }
    
    // Si estamos editando o en el modal, siempre validar los campos del formulario
    const fieldsToValidate = ['companyName', 'entryDate', 'workAddress', 'workPostalCode', 'workProvince', 'workLocality'];
    const newErrors = {};
    fieldsToValidate.forEach((field) => {
      const value = formData[field];
      const error = validateField(field, value);
      if (error) {
        newErrors[field] = error;
      }
    });
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    setSectionValidity((prev) => ({ ...prev, [sectionName]: isValid }));
    return isValid;
  };

  const handleProvinceChange = async (e) => {
    const { name, value } = e.target;
    handleChange(e);
    
    // Resetear la localidad y su error cuando cambia la provincia
    handleChange({
      target: { 
        name: 'workLocality',
        value: ''
      }
    });

    // Removemos el estado "tocado" de la localidad y provincia cuando cambian
    setTouchedFields(prev => ({
      ...prev,
      workLocality: false,
      workProvince: false
    }));

    setErrors(prev => ({
      ...prev,
      workLocality: '',
      workProvince: ''
    }));
    
    if (value) {
      const cachedLocalities = getLocalitiesFromCache(value);
      if (cachedLocalities) {
        setWorkLocalities(cachedLocalities);
      } else {
        setIsLoadingLocalities(true);
        try {
          const response = await api.get(`/provinces/${value}/localities`);
          setWorkLocalities(response.data);
          saveLocalitiesToCache(value, response.data);
        } catch (error) {
          console.error('Error fetching localities:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudieron cargar las localidades. Por favor, intente nuevamente.',
          });
        } finally {
          setIsLoadingLocalities(false);
        }
      }
    } else {
      setWorkLocalities([]);
    }
  };
  
  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/\D/g, '');
    if (numericValue.length <= 13) {
      handleFieldChange({
        target: { name, value: numericValue },
      });
    }
  };

  const handleLocalityChange = (e) => {
    const { name, value } = e.target;
    handleFieldChange(e);

    const selectedLocality = workLocalities.find((loc) => loc.id === Number(value));
    if (selectedLocality) {
      handleFieldChange({
        target: { name: 'workPostalCode', value: selectedLocality.postal_code },
      });
      validateField('workPostalCode', selectedLocality.postal_code);
    }
  };

  const getLocalitiesFromCache = (provinceId) => {
    const cachedData = localStorage.getItem(`localities_${provinceId}`);
    return cachedData ? JSON.parse(cachedData) : null;
  };
  
  const saveLocalitiesToCache = (provinceId, localities) => {
    localStorage.setItem(`localities_${provinceId}`, JSON.stringify(localities));
  };

  // Helper function para obtener el nombre legible de la provincia
  const getProvinceName = (provinceValue) => {
    if (!provinceValue) return '';
    // Si ya es un nombre (string), devolverlo
    if (typeof provinceValue === 'string' && isNaN(provinceValue)) {
      return provinceValue;
    }
    // Si es un ID, buscar el nombre
    const province = provinces.find(p => p.id === Number(provinceValue));
    return province ? province.name : provinceValue;
  };

  // Helper function para formatear la ubicación (sin dirección)
  const formatEmploymentLocation = (suggestion) => {
    let locality, province, postalCode;
    
    if (suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_')) {
      // Para empleos nuevos/editados
      locality = suggestion.address?.locality_label || suggestion.address?.locality || '';
      province = suggestion.address?.province_label || getProvinceName(suggestion.address?.province);
      postalCode = suggestion.address?.postal_code || '';
    } else {
      // Para empleos existentes
      locality = suggestion.employer?.locality || '';
      province = suggestion.employer?.province || '';
      postalCode = suggestion.employer?.postal_code || '';
    }
    
    // Construir el formato: "Localidad, Provincia (CP)"
    const locationParts = [locality, province].filter(part => part && String(part).trim());
    let locationString = locationParts.join(', ');
    
    // Agregar código postal entre paréntesis si existe
    if (postalCode && String(postalCode).trim()) {
      locationString += ` (${postalCode})`;
    }
    
    return locationString;
  };

  // useEffect para manejar la selección de localidad cuando se edita un empleo
  useEffect(() => {
    if (isEditingEmployment && editingEmploymentData && workLocalities.length > 0 && !formData.workLocality) {
      let localityToFind;
      
      // Determinar qué localidad buscar según el tipo de empleo
      if (editingEmploymentData.id.toString().startsWith('new_') || editingEmploymentData.id.toString().startsWith('edited_')) {
        localityToFind = editingEmploymentData.address.locality;
      } else {
        localityToFind = editingEmploymentData.employer.locality;
      }
      
      // Buscar la localidad por ID o nombre
      let localityToSelect = null;
      if (!isNaN(localityToFind)) {
        localityToSelect = workLocalities.find(loc => loc.id === Number(localityToFind));
      } else if (typeof localityToFind === 'string') {
        localityToSelect = workLocalities.find(loc => 
          loc.name && loc.name.toLowerCase() === localityToFind.toLowerCase()
        );
      }
      
      if (localityToSelect) {
        handleChange({ target: { name: 'workLocality', value: localityToSelect.id } });
      }
    }
  }, [workLocalities, isEditingEmployment, editingEmploymentData, formData.workLocality]);

  useImperativeHandle(ref, () => ({
    validateSection
  }));

  // Renderizado condicional basado en el modo de visualización
  if (isContinuousMode) {
    return (
      <div className="continuous-section mb-2">
        <div className="section-header mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="d-flex align-items-center mb-0 fw-light">
              <FontAwesomeIcon icon={faBriefcase} className="me-2" />
              Información Laboral
              {sectionValidity[sectionName] === true && (
                <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
              )}
              {sectionValidity[sectionName] === false && (
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
              )}
            </h5>
            <button 
              className="btn btn-sm btn-outline-primary"
              onClick={handleUseNewForm}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Nuevo Empleo
            </button>
          </div>
        </div>
        <div className="section-content">
          {isLoadingSuggestions ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="mt-2 text-muted">Cargando empleos sugeridos...</p>
            </div>
          ) : (
            <div className="suggested-data">
              <div className="mb-3">
                <h6 className="mb-0">Gestione su información laboral:</h6>
              </div>
              <div className="row g-1">
                {displayedSuggestions.map(suggestion => (
                  <div key={`${suggestion.id}-${suggestion.created_at}`} className="col-12">
                    <div className="card suggested-address-card mb-1 position-relative">
                      <div className="card-body">
                        <div className="form-check d-flex align-items-center">
                          <input
                            className="form-check-input mt-0"
                            type="radio"
                            name="employmentSelection"
                            id={`employment-${suggestion.id}`}
                            onChange={() => handleUseExisting(suggestion)}
                            checked={selectedData.employment === suggestion.id}
                            style={{ marginTop: 0, marginBottom: 0 }}
                          />
                          <label className="form-check-label w-100 ms-2" htmlFor={`employment-${suggestion.id}`} style={{ cursor: 'pointer' }}>
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <p className="mb-1 fw-medium lh-1">{suggestion.employer.name}</p>
                                {suggestion.job.title && (
                                  <small className="text-muted d-block lh-1 mb-1" style={{ fontSize: '0.75rem' }}>
                                    <FontAwesomeIcon icon={faUserTie} className="me-1" style={{ fontSize: '0.7rem' }} />
                                    {suggestion.job.title}
                                  </small>
                                )}
                                {formatEmploymentLocation(suggestion) && (
                                  <small className="text-muted d-block lh-1" style={{ fontSize: '0.75rem' }}>
                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" style={{ fontSize: '0.7rem' }} />
                                    {formatEmploymentLocation(suggestion)}
                                  </small>
                                )}
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                {suggestion.id.toString().startsWith('new_') && (
                                  <span className="badge bg-primary">Nuevo</span>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                        <DataSourceBadge dataItem={suggestion} options={{ position: 'top-right' }} />
                        {/* Botón de edición - mostrar para empleos originales y editados, pero no para nuevos */}
                        {(!suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_')) && (
                          <EditButton 
                            onEdit={() => handleEditEmployment(suggestion)} 
                            options={{ position: 'bottom-right', variant: 'muted' }} 
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="col-12 mt-3 d-flex justify-content-between align-items-center">
                  <div>
                    {!showAllSuggestions && uniqueSuggestions.length > displayedSuggestions.length && (
                      <button 
                        className="btn btn-link text-decoration-none"
                        onClick={() => setShowAllSuggestions(true)}
                      >
                        Ver {uniqueSuggestions.length - displayedSuggestions.length} más
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal para nuevo empleo */}
        <Modal show={showNewEmploymentModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{isEditingEmployment ? 'Editar Empleo' : 'Nuevo Empleo'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Empresa<sup>*</sup></label>
                <input
                  type="text"
                  className={`form-control ${(errors.companyName && (formSubmitted || touchedFields.companyName)) ? 'is-invalid' : ''}`}
                  name="companyName"
                  value={formData.companyName || ''}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  required
                  autoComplete="off"
                />
                {(errors.companyName && (formSubmitted || touchedFields.companyName)) && <div className="invalid-feedback">{errors.companyName}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Fecha de Ingreso<sup>*</sup></label>
                <input
                  type="date"
                  className={`form-control ${(errors.entryDate && (formSubmitted || touchedFields.entryDate)) ? 'is-invalid' : ''}`}
                  name="entryDate"
                  value={formData.entryDate || ''}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  autoComplete="off"
                  required
                />
                {(errors.entryDate && (formSubmitted || touchedFields.entryDate)) && <div className="invalid-feedback">{errors.entryDate}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Cargo</label>
                <input
                  type="text"
                  className="form-control"
                  name="position"
                  value={formData.position || ''}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  autoComplete="off"
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Teléfono Laboral</label>
                <input
                  type="tel"
                  className="form-control"
                  name="workPhone"
                  value={formData.workPhone || ''}
                  onChange={handlePhoneChange}
                  onBlur={handleFieldBlur}
                  autoComplete="off"
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Domicilio Laboral<sup>*</sup></label>
                <input
                  type="text"
                  className={`form-control ${(errors.workAddress && (formSubmitted || touchedFields.workAddress)) ? 'is-invalid' : ''}`}
                  name="workAddress"
                  value={formData.workAddress || ''}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  autoComplete="off"
                  required
                />
                {(errors.workAddress && (formSubmitted || touchedFields.workAddress)) && <div className="invalid-feedback">{errors.workAddress}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Provincia<sup>*</sup></label>
                <select
                  className={`form-control ${(errors.workProvince && (formSubmitted || touchedFields.workProvince)) ? 'is-invalid' : ''}`}
                  name="workProvince"
                  value={formData.workProvince || ''}
                  onChange={handleProvinceChange}
                  onBlur={handleFieldBlur}
                  autoComplete="off"
                  required
                >
                  <option value="" disabled>Seleccione una provincia</option>
                  {provinces.map((prov) => (
                    <option key={prov.id} value={prov.id}>{prov.name}</option>
                  ))}
                </select>
                {(errors.workProvince && (formSubmitted || touchedFields.workProvince)) && <div className="invalid-feedback">{errors.workProvince}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Localidad<sup>*</sup></label>
                <div className="position-relative">
                  <select
                    className={`form-control ${(errors.workLocality && !isLoadingLocalities && (formSubmitted || touchedFields.workLocality) && workLocalities.length > 0) ? 'is-invalid' : ''}`}
                    name="workLocality"
                    value={formData.workLocality || ''}
                    onChange={handleLocalityChange}
                    onBlur={handleFieldBlur}
                    autoComplete="off"
                    required
                    disabled={!formData.workProvince || isLoadingLocalities}
                  >
                    <option value="" disabled>
                      {!formData.workProvince 
                        ? 'Primero seleccione una provincia'
                        : isLoadingLocalities 
                          ? 'Cargando localidades...'
                          : 'Seleccione una localidad'
                      }
                    </option>
                    {workLocalities.map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                  {isLoadingLocalities && (
                    <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                      </div>
                    </div>
                  )}
                  {(errors.workLocality && !isLoadingLocalities && (formSubmitted || touchedFields.workLocality) && workLocalities.length > 0) && (
                    <div className="invalid-feedback">{errors.workLocality}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Código Postal Laboral<sup>*</sup></label>
                <input
                  type="text"
                  className={`form-control ${(errors.workPostalCode && (formSubmitted || touchedFields.workPostalCode)) ? 'is-invalid' : ''}`}
                  name="workPostalCode"
                  value={formData.workPostalCode || ''}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  autoComplete="off"
                  required
                />
                {(errors.workPostalCode && (formSubmitted || touchedFields.workPostalCode)) && <div className="invalid-feedback">{errors.workPostalCode}</div>}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="d-flex justify-content-between w-100">
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleSaveNewEmployment}>
                {isEditingEmployment ? 'Guardar Cambios' : 'Guardar Empleo'}
              </button>
            </div>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  // Modo accordion (original)
  return (
    <div className="accordion-item">
      <h2 className="accordion-header" id={`heading${sectionName}`}>
        <button
          className={`accordion-button ${activeSection !== sectionName ? 'collapsed' : ''}`}
          type="button"
          onClick={() => handleSectionClick(sectionName)}
        >
          <FontAwesomeIcon icon={faBriefcase} className="me-2" />
          Información Laboral{' '}
          {sectionValidity[sectionName] === true && (
            <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
          )}
          {sectionValidity[sectionName] === false && (
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
          )}
        </button>
      </h2>
      <div
        className={`accordion-collapse collapse ${activeSection === sectionName ? 'show' : ''}`}
        aria-labelledby={`heading${sectionName}`}
      >
        <div className="accordion-body">
          {isLoadingSuggestions ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="mt-2 text-muted">Cargando empleos sugeridos...</p>
            </div>
          ) : (
            <div className="suggested-data">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Gestione su información laboral:</h6>
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleUseNewForm}
                >
                  <i className="bi bi-plus-circle me-1"></i>
                  Nuevo Empleo
                </button>
              </div>
              <div className="row g-1">
                {displayedSuggestions.map(suggestion => (
                  <div key={`${suggestion.id}-${suggestion.created_at}`} className="col-12">
                    <div className="card suggested-address-card mb-1 position-relative">
                      <div className="card-body">
                        <div className="form-check d-flex align-items-center">
                          <input
                            className="form-check-input mt-0"
                            type="radio"
                            name="employmentSelection"
                            id={`employment-${suggestion.id}`}
                            onChange={() => handleUseExisting(suggestion)}
                            checked={selectedData.employment === suggestion.id}
                            style={{ marginTop: 0, marginBottom: 0 }}
                          />
                          <label className="form-check-label w-100 ms-2" htmlFor={`employment-${suggestion.id}`} style={{ cursor: 'pointer' }}>
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <p className="mb-1 fw-medium lh-1">{suggestion.employer.name}</p>
                                {suggestion.job.title && (
                                  <small className="text-muted d-block lh-1 mb-1" style={{ fontSize: '0.75rem' }}>
                                    <FontAwesomeIcon icon={faUserTie} className="me-1" style={{ fontSize: '0.7rem' }} />
                                    {suggestion.job.title}
                                  </small>
                                )}
                                {formatEmploymentLocation(suggestion) && (
                                  <small className="text-muted d-block lh-1" style={{ fontSize: '0.75rem' }}>
                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" style={{ fontSize: '0.7rem' }} />
                                    {formatEmploymentLocation(suggestion)}
                                  </small>
                                )}
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                {suggestion.id.toString().startsWith('new_') && (
                                  <span className="badge bg-primary">Nuevo</span>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                        {/* Badge de origen de datos */}
                        <DataSourceBadge dataItem={suggestion} options={{ position: 'top-right' }} />
                        {/* Botón de edición - mostrar para empleos originales y editados, pero no para nuevos */}
                        {(!suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_')) && (
                          <EditButton 
                            onEdit={() => handleEditEmployment(suggestion)} 
                            options={{ position: 'bottom-right', variant: 'muted' }} 
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="col-12 mt-3 d-flex justify-content-between align-items-center">
                  <div>
                    {!showAllSuggestions && uniqueSuggestions.length > displayedSuggestions.length && (
                      <button 
                        className="btn btn-link text-decoration-none"
                        onClick={() => setShowAllSuggestions(true)}
                      >
                        Ver {uniqueSuggestions.length - displayedSuggestions.length} más
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal para nuevo empleo */}
      <Modal show={showNewEmploymentModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditingEmployment ? 'Editar Empleo' : 'Nuevo Empleo'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Empresa<sup>*</sup></label>
              <input
                type="text"
                className={`form-control ${(errors.companyName && (formSubmitted || touchedFields.companyName)) ? 'is-invalid' : ''}`}
                name="companyName"
                value={formData.companyName || ''}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                required
                autoComplete="off"
              />
              {(errors.companyName && (formSubmitted || touchedFields.companyName)) && <div className="invalid-feedback">{errors.companyName}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Fecha de Ingreso<sup>*</sup></label>
              <input
                type="date"
                className={`form-control ${(errors.entryDate && (formSubmitted || touchedFields.entryDate)) ? 'is-invalid' : ''}`}
                name="entryDate"
                value={formData.entryDate || ''}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                autoComplete="off"
                required
              />
              {(errors.entryDate && (formSubmitted || touchedFields.entryDate)) && <div className="invalid-feedback">{errors.entryDate}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Cargo</label>
              <input
                type="text"
                className="form-control"
                name="position"
                value={formData.position || ''}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                autoComplete="off"
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Teléfono Laboral</label>
              <input
                type="tel"
                className="form-control"
                name="workPhone"
                value={formData.workPhone || ''}
                onChange={handlePhoneChange}
                onBlur={handleFieldBlur}
                autoComplete="off"
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Domicilio Laboral<sup>*</sup></label>
              <input
                type="text"
                className={`form-control ${(errors.workAddress && (formSubmitted || touchedFields.workAddress)) ? 'is-invalid' : ''}`}
                name="workAddress"
                value={formData.workAddress || ''}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                autoComplete="off"
                required
              />
              {(errors.workAddress && (formSubmitted || touchedFields.workAddress)) && <div className="invalid-feedback">{errors.workAddress}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Provincia<sup>*</sup></label>
              <select
                className={`form-control ${(errors.workProvince && (formSubmitted || touchedFields.workProvince)) ? 'is-invalid' : ''}`}
                name="workProvince"
                value={formData.workProvince || ''}
                onChange={handleProvinceChange}
                onBlur={handleFieldBlur}
                autoComplete="off"
                required
              >
                <option value="" disabled>Seleccione una provincia</option>
                {provinces.map((prov) => (
                  <option key={prov.id} value={prov.id}>{prov.name}</option>
                ))}
              </select>
              {(errors.workProvince && (formSubmitted || touchedFields.workProvince)) && <div className="invalid-feedback">{errors.workProvince}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Localidad<sup>*</sup></label>
              <div className="position-relative">
                <select
                  className={`form-control ${(errors.workLocality && !isLoadingLocalities && (formSubmitted || touchedFields.workLocality) && workLocalities.length > 0) ? 'is-invalid' : ''}`}
                  name="workLocality"
                  value={formData.workLocality || ''}
                  onChange={handleLocalityChange}
                  onBlur={handleFieldBlur}
                  autoComplete="off"
                  required
                  disabled={!formData.workProvince || isLoadingLocalities}
                >
                  <option value="" disabled>
                    {!formData.workProvince 
                      ? 'Primero seleccione una provincia'
                      : isLoadingLocalities 
                        ? 'Cargando localidades...'
                        : 'Seleccione una localidad'
                    }
                  </option>
                  {workLocalities.map((loc) => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
                {isLoadingLocalities && (
                  <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                  </div>
                )}
                {(errors.workLocality && !isLoadingLocalities && (formSubmitted || touchedFields.workLocality) && workLocalities.length > 0) && (
                  <div className="invalid-feedback">{errors.workLocality}</div>
                )}
              </div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Código Postal Laboral<sup>*</sup></label>
              <input
                type="text"
                className={`form-control ${(errors.workPostalCode && (formSubmitted || touchedFields.workPostalCode)) ? 'is-invalid' : ''}`}
                name="workPostalCode"
                value={formData.workPostalCode || ''}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                autoComplete="off"
                required
              />
              {(errors.workPostalCode && (formSubmitted || touchedFields.workPostalCode)) && <div className="invalid-feedback">{errors.workPostalCode}</div>}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <button className="btn btn-secondary" onClick={handleCloseModal}>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={handleSaveNewEmployment}>
              {isEditingEmployment ? 'Guardar Cambios' : 'Guardar Empleo'}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default EmploymentInfo;



